import axiosInstance from './axiosInstance'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Permission {
  id: string
  action: string
  description: string | null
  createdAt: string
}

export interface RolePermission {
  permission: Permission
}

export interface Role {
  id: string
  name: string
  description: string | null
  orgId: string | null          // null = global system role, non-null = org-specific custom role
  permissions: RolePermission[]
  count: { users: number }
  createdAt: string
  updatedAt: string
}

export interface CreateRolePayload {
  name: string
  description?: string
  permissionIds?: string[]
}

export interface UpdateRolePayload {
  name?: string
  description?: string
  permissionIds?: string[]
}

// ── API ───────────────────────────────────────────────────────────────────────

export const rolesApi = {
  getAll: async (): Promise<Role[]> => {
    const res = await axiosInstance.get<{ data: Role[] }>('/roles')
    return res.data.data
  },

  getById: async (id: string): Promise<Role> => {
    const res = await axiosInstance.get<{ data: Role }>(`/roles/${id}`)
    return res.data.data
  },

  create: async (payload: CreateRolePayload): Promise<Role> => {
    const res = await axiosInstance.post<{ data: Role }>('/roles', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateRolePayload): Promise<Role> => {
    const res = await axiosInstance.put<{ data: Role }>(`/roles/${id}`, payload)
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/roles/${id}`)
  },

  assignPermissions: async (id: string, permissionIds: string[]): Promise<Role> => {
    const res = await axiosInstance.put<{ data: Role }>(`/roles/${id}/permissions`, { permissionIds })
    return res.data.data
  },

  getAllPermissions: async (): Promise<Permission[]> => {
    const res = await axiosInstance.get<{ data: Permission[] }>('/permissions')
    return res.data.data
  },
}
